<template>
  <div>
    <b-sidebar
      id="assign-users-drawer"
      class="assign-drawer"
      :backdrop-variant="'dark'"
      backdrop
      shadow
      right
      no-header
      :visible="value"
      @change="$emit('input', $event)"
      @shown="onShown"
      @hidden="onHidden"
    >
      <div class="assign-body">
        <div class="d-flex align-items-start justify-content-between mb-3">
          <div class="pr-3">
            <h5 class="mb-1">{{ $t("formsEditor.assignUsers") }}</h5>
            <p class="f-12 text-grey mb-0">{{ $t("formsEditor.assignUsersHint") }}</p>
          </div>
        </div>

        <!-- A ticked list rather than a chip picker: with a few hundred drivers,
             chips say nothing about who is and isn't in. -->
        <b-form-group :label="$t('formsEditor.selectDrivers')" class="text-left mb-3">
          <b-input-group class="username-input-field mb-2">
            <b-form-input v-model="driverSearch" class="unit f-12"
              :placeholder="$t('formsEditor.searchDrivers')" @keyup.enter="reloadDriverRows"></b-form-input>
            <b-input-group-append>
              <b-button variant="primary" @click="reloadDriverRows">
                <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M6.14286 11.7857C8.98318 11.7857 11.2857 9.48318 11.2857 6.64286C11.2857 3.80254 8.98318 1.5 6.14286 1.5C3.30254 1.5 1 3.80254 1 6.64286C1 9.48318 3.30254 11.7857 6.14286 11.7857Z"
                    stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  <path d="M10 10.5L13 13.5" stroke="white" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
                </svg>
              </b-button>
            </b-input-group-append>
          </b-input-group>
          <!-- Scrolls to the end to pull the next page. -->
          <div class="access-picker" @scroll="onDriverScroll">
            <div class="access-row d-flex align-items-center" v-for="option in driverRows" :key="option.id">
              <b-form-checkbox class="mr-2 pl-0" :checked="isDriverPicked(option.id)"
                @change="toggleDriver(option.id)"></b-form-checkbox>
              <span class="f-14 text-black">{{ option.label }}</span>
            </div>
            <p class="f-12 text-grey text-center mb-0 p-3" v-if="driverLoading">
              {{ $t("formsEditor.loading") }}
            </p>
            <p class="f-12 text-grey text-center mb-0 p-3" v-else-if="!driverRows.length">
              {{ $t("formsEditor.noDriversFound") }}
            </p>
          </div>
          <div class="d-flex align-items-center flex-wrap mt-2">
            <!-- A switch: the API assigns to every driver in one go, and the
                 rows on screen tick along so it is clear what it means. -->
            <b-button :variant="allDrivers ? 'primary' : 'outline-primary'" size="sm" class="f-12 mr-2"
              @click="toggleAllDrivers">
              {{ $t("formsEditor.selectAll") }}
            </b-button>
            <!-- Just the page that is loaded, for picking a batch by hand. -->
            <b-button variant="outline-primary" size="sm" class="f-12" :disabled="!addableDrivers.length"
              @click="addAllDrivers">
              {{ $t("formsEditor.addAllShown", { count: driverRows.length }) }}
            </b-button>
            <span class="ml-auto f-12 text-grey">
              {{ $t("formsEditor.shownOfTotal", { shown: driverRows.length, total: driverTotal }) }}
            </span>
          </div>
          <p class="f-12 text-grey mb-0 mt-2">
            {{ allDrivers
              ? $t("formsEditor.allDriversSelected")
              : $t("formsEditor.selectedCount", { count: assignSelected.length }) }}
          </p>
        </b-form-group>
        <b-form-group :label="$t('formsEditor.dueDate')" class="text-left mb-3">
          <date-picker
            v-model="assignDueDate"
            :formatter="momentFormat"
            class="date-picker-custom f-12 w-100"
            :placeholder="$t('formsEditor.selectDate')"
          ></date-picker>
        </b-form-group>
      </div>
      <div class="sidebar-footer position-fixed">
        <div class="d-flex align-items-center justify-content-end">
          <div class="text-right modal-footer border-0 p-0">
            <b-button class="mx-0 mr-3 h-40px w-100px m-0" variant="secondary" @click="close">
              {{ $t('formsEditor.close') }}
            </b-button>
            <b-button :disabled="!allDrivers && !assignSelected.length" @click="assignUsers"
              class="mx-0 mb-0 h-40px w-100px m-0" variant="primary">
              {{ $t('formsEditor.assign') }}
            </b-button>
          </div>
        </div>
      </div>
    </b-sidebar>
  </div>
</template>
<script>
import { mapActions } from "vuex";
import * as moment from "moment-timezone";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

export default {
  name: "AssignDriversDrawer",
  components: { DatePicker },
  props: {
    // Open state, so the drawer works with v-model.
    value: {
      type: Boolean,
      default: false,
    },
    // The template being handed out.
    templateId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      driverSearch: "",
      driverRows: [],
      driverLoading: false,
      driverPage: 1,
      driverTotalPages: 1,
      driverTotal: 0,
      assignSelected: [],
      // "Select All" hands the job to the API rather than ticking every row.
      allDrivers: false,
      assignDueDate: new Date(),
      momentFormat: {
        stringify: (date) => (date ? moment(date).format("ll") : ""),
        parse: (value) => (value ? moment(value).toDate() : null),
      },
    };
  },
  computed: {
    addableDrivers() {
      return this.driverRows.filter((option) => !this.isDriverPicked(option.id));
    },
  },
  methods: {
    ...mapActions(["getAllDrivers", "assignFormTemplate", "setNotification", "setLoader"]),
    // The drawer scrolls on its own, so the page behind it holds still while it
    // is open — two scrollbars at once is what made it feel loose.
    lockPageScroll(locked) {
      document.body.style.overflow = locked ? "hidden" : "";
    },
    // A fresh drawer every time: today's date, nothing ticked, page one.
    onShown() {
      this.lockPageScroll(true);
      this.assignDueDate = new Date();
      this.assignSelected = [];
      this.allDrivers = false;
      this.driverSearch = "";
      this.reloadDriverRows();
    },
    onHidden() {
      this.lockPageScroll(false);
    },
    close() {
      this.$emit("input", false);
    },
    isDriverPicked(id) {
      return this.assignSelected.some((entry) => `${entry}` === `${id}`);
    },
    toggleDriver(id) {
      this.assignSelected = this.isDriverPicked(id)
        ? this.assignSelected.filter((entry) => `${entry}` !== `${id}`)
        : [...this.assignSelected, id];
    },
    // Ticking every row on screen says what "all drivers" means; the payload
    // still asks the API to take the lot, loaded or not.
    toggleAllDrivers() {
      this.allDrivers = !this.allDrivers;
      if (!this.allDrivers) {
        this.assignSelected = [];
        return;
      }
      this.addAllDrivers();
    },
    addAllDrivers() {
      this.assignSelected = [
        ...this.assignSelected,
        ...this.addableDrivers.map((option) => option.id),
      ];
    },
    reloadDriverRows() {
      this.loadDriverRows(true);
    },
    // Drivers arrive a page at a time, so the list grows as it is scrolled.
    async loadDriverRows(reset) {
      if (this.driverLoading) return;
      const page = reset ? 1 : this.driverPage;
      if (!reset && page > this.driverTotalPages) return;
      this.driverLoading = true;
      try {
        const data = await this.getAllDrivers(
          `page=${page}&page_size=100&name=${this.driverSearch.trim()}`
        );
        const rows = ((data && data.records) || []).map((driver) => {
          const user = driver.user || {};
          return {
            id: `${driver.id}`,
            label: `${user.first_name || ""} ${user.last_name || ""}`.trim() || driver.name || `${driver.id}`,
          };
        });
        const merged = reset ? rows : [...this.driverRows, ...rows];
        // Ids can arrive twice when a page boundary shifts between calls.
        this.driverRows = [...new Map(merged.map((row) => [row.id, row])).values()];
        // "Select All" is on, so whatever just arrived is ticked too.
        if (this.allDrivers) this.addAllDrivers();
        this.driverTotalPages = (data && data.total_pages) || 1;
        this.driverTotal = (data && data.total_entries) || this.driverRows.length;
        this.driverPage = page + 1;
      } catch (error) {
        console.error("Error loading drivers:", error);
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.driverLoading = false;
      }
    },
    onDriverScroll(event) {
      const el = event.target;
      if (el.scrollTop + el.clientHeight < el.scrollHeight - 40) return;
      this.loadDriverRows(false);
    },
    async assignUsers() {
      if (!this.templateId) return;
      if (!this.allDrivers && !this.assignSelected.length) return;
      const dueDate = this.assignDueDate ? moment(this.assignDueDate).format("YYYY-MM-DD") : null;
      try {
        this.setLoader(true);
        // Everyone at once, or just the drivers that were ticked.
        const payload = this.allDrivers
          ? { all_drivers: true, due_date: dueDate }
          : {
              assignments: this.assignSelected.map((memberId) => ({
                member_id: Number(memberId),
                due_date: dueDate,
              })),
            };
        await this.assignFormTemplate({ id: this.templateId, payload });
        this.assignSelected = [];
        this.allDrivers = false;
        await this.setNotification({
          msg: this.$t("formsEditor.assigned"),
          type: "success",
          color: "green",
        });
        // Whoever opened the drawer may want to re-read what changed.
        this.$emit("assigned");
        this.close();
      } catch (error) {
        await this.setNotification({ msg: this.apiError(error), type: "error", color: "red" });
      } finally {
        this.setLoader(false);
      }
    },
    apiError(error) {
      if (typeof error === "string") return error;
      if (error && error.error) return error.error;
      if (error && error.detail) return error.detail.error || error.detail;
      const firstField = error && Object.values(error)[0];
      if (Array.isArray(firstField)) return firstField[0];
      return firstField || this.$t("formsEditor.saveFailed");
    },
  },
  beforeDestroy() {
    // Leaving with the drawer open must not leave the page unscrollable.
    this.lockPageScroll(false);
  },
};
</script>
<style scoped>
.assign-drawer >>> .b-sidebar {
  width: 443px;
  max-width: 100%;
}
.assign-body {
  padding: 24px 16px;
  text-align: left;
}
/* The driver picker: a scrolling list of names, ticked in place. It takes the
   height the drawer has going spare. */
.access-picker {
  max-height: calc(100vh - 440px);
  min-height: 280px;
  overflow-y: auto;
  border: 1px solid #e3e3e3;
  border-radius: 4px;
}
.access-row {
  padding: 8px 12px;
  border-bottom: 1px solid #f2f2f2;
}
.access-row:last-child {
  border-bottom: none;
}
</style>
